import styled from '@emotion/styled';
import { Skeleton } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { ChartData, ChartOptions, TooltipCallbacks, TooltipItem, TooltipModel } from 'chart.js';
import { useRef } from 'react';
import { Line } from 'react-chartjs-2';
import { BasicChartProps } from '../DashboardWidget';
import ChartLegend from './ChartLegend/ChartLegend';
import { defaultChartAnimation, chartMaxHeight } from './chartConfig';
import { MatrixedChartData, buildChartColors, legendMouseLeave, legendMouseOver } from './chartUtils';
import NoDataOverlay from './NoDataOverlay';
import { DeepPartial } from 'chart.js/types/utils';
import { deepMerge } from '@/_utils/utils';

export interface LineChartData extends MatrixedChartData {
  noDataMessage: null | string;
  ymin?: number;
  ymax?: number;
  ttlabel?: (this: TooltipModel<'line'>, tooltipItem: TooltipItem<'line'>) => string | string[];
}

interface LineChartProps extends BasicChartProps {
  data: LineChartData;
  height?: number;
  options?: DeepPartial<ChartOptions<'line'>>;
}

const LineChart = ({ title, data, colors: colorMap, loading, animation = defaultChartAnimation, height, options }: LineChartProps) => {
  // const calculatedMaxAndStepSize = calculateMaxAndStepSize(techReviewData, editData, deliveryData, totalJobsData);
  const chartHeight = height || chartMaxHeight;
  const lineRef = useRef();
  const theme = useTheme();
  const chartColors = buildChartColors(data.values.length || 1, data.colors, colorMap);

  function buildDataSets(data: LineChartData) {
    const datasets: ChartData<'line', unknown, unknown>['datasets'] = [];

    let num = 100;

    for (let i = 0; i < data.values.length; i++) {
      datasets.push({
        label: data.values[i].label,
        data: data.values[i].data,
        fill: true,
        borderColor: chartColors[i].start,
        tension: 0.4,
        pointRadius: 0,

        backgroundColor: (context: any) => {
          if (!context.chart.chartArea) return;

          const datasetIndex = context.datasetIndex;
          const data = context.chart.data.datasets[datasetIndex].data;
          const maxValue = Math.max(...data);

          const yAxis = context.chart.scales.y;
          const maxY = yAxis.getPixelForValue(maxValue);

          const gradient = context.chart.ctx.createLinearGradient(0, maxY, 0, context.chart.chartArea.bottom);

          // make gradient slightly transparent for light mode
          gradient.addColorStop(0, chartColors[i].start + (theme.palette.mode === 'light' ? '4a' : ''));
          gradient.addColorStop(1, 'rgba(0, 0, 0, 0)');
          return gradient;
        }
      });
      num += 100;
    }

    return datasets;
  }

  // ChartJS Options
  const chartOptions: ChartOptions<'line'> = {
    maintainAspectRatio: false,
    responsive: true,
    animation: animation,
    plugins: {
      title: {
        font: {
          family: theme.typography.h1.fontFamily
        },
        display: true,
        text: data.axes.y,
        position: 'left',
        color: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[300]
      },
      tooltip: {
        mode: 'nearest',
        intersect: false,
        enabled: true,
        backgroundColor: theme.palette.background.default,
        titleColor: theme.palette.text.primary,
        bodyColor: theme.palette.text.primary,
        displayColors: false,
        ...(data.ttlabel ? { callbacks: { label: data.ttlabel } } : {})
      },
      legend: {
        display: false
      }
    },
    scales: {
      x: {
        grid: {
          display: false
        },
        ticks: {
          color: theme.palette.grey[500],
          font: {
            family: theme.typography.h1.fontFamily
          }
        }
      },
      y: {
        min: data.ymin || 0,
        ...(data.ymax ? { max: data.ymax } : {}),
        grid: {
          color: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[300]
        },
        ticks: {
          font: {
            family: theme.typography.h1.fontFamily
          },
          color: theme.palette.grey[500],
          precision: 0,
          callback: (value: any) => {
            if (typeof value === 'number') return value;
          }
          // stepSize: calculatedMaxAndStepSize.stepSize
        }
        // max: calculatedMaxAndStepSize.max
      }
    }
  };

  // ChartJS Data
  const chartData = {
    labels: data.labels,
    datasets: buildDataSets(data)
  };

  return (
    <Wrapper legend={data.legend}>
      {data.noDataMessage && !loading && <NoDataOverlay message={data.noDataMessage} />}
      <Chart>
        {loading ? (
          <ChartSkeleton height={chartHeight} variant="rectangular" />
        ) : (
          <Line ref={lineRef} data={chartData} options={deepMerge(chartOptions, options)} height={chartHeight} />
        )}
      </Chart>
      <Details id="details">
        <ChartLegend
          data={data}
          colors={chartColors}
          loading={loading}
          skeletonOrientation="horizontal"
          mouseOverCallback={(i: any) => legendMouseOver(lineRef, i, true)}
          mouseLeaveCallback={() => legendMouseLeave(lineRef)}
        />
        {data.legend !== 'tile' && <Notice>{loading ? <NoticeSkeleton variant="text" width="75%" /> : `Displaying up to 6 months`}</Notice>}
      </Details>
    </Wrapper>
  );
};

export default LineChart;

const Wrapper = styled.div<{ legend: MatrixedChartData['legend'] }>`
  display: flex;
  position: relative;
  flex-direction: ${({ legend }) => (legend === 'tile' ? 'row-reverse' : 'column')};
  justify-content: center;
  align-items: center;
  padding: ${({ theme }) => theme.spacing(2)};
  margin: auto;
  position: relative;
  width: 100%;

  ${({ legend }) =>
    legend !== 'tile' &&
    `
  #details {
    width: 100%;
  }
  `}
`;

const Chart = styled.div`
  position: relative;
  height: 100%;
  display: flex;
  width: 100%;
  margin: auto;

  > canvas {
    max-width: 100%;
  }
`;

const Details = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: ${({ theme }) => theme.spacing(2, 2, 0, 2)};
`;

const Notice = styled.div`
  opacity: 0.4;
  padding-left: ${({ theme }) => theme.spacing(2)};
  white-space: nowrap;
  display: flex;
  justify-content: flex-end;
  font-size: 1.5rem;

  @media (min-width: ${({ theme }) => theme.breakpoints.values['md']}px) {
    font-size: 1.1rem;
  }
`;

const ChartSkeleton = styled(Skeleton)<{ height: number }>`
  width: 100%;
  // height: ${({ height }) => height}px;
`;

const NoticeSkeleton = styled(Skeleton)`
  .MuiSkeleton-root {
    max-width: 300px;
  }
`;
